import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeftIcon,
  FolderIcon,
  DocumentTextIcon,
  RocketLaunchIcon,
  UserPlusIcon,
  Cog6ToothIcon,
  XMarkIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FunnelIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";
import { apiService } from "../services/apiService";
import { Workflow, ProjectFormElement } from "../types";
import { useTranslation } from "react-i18next";
import { ProjectFormBuilder } from "../components/forms/ProjectFormBuilder";
import { WorkflowStartModal } from "../components/workflow/WorkflowStartModal";
import { ConfirmDialog } from "../components/common/ConfirmDialog";
import toast from "../utils/toast";
import { useAuth } from "../contexts/AuthContext";

interface WorkflowInstanceProject {
  id: number;
  name: string;
  description?: string;
  status: string;
  workflowContainerId: number;
  templateWorkflowId?: number;
  startedAt?: string;
  completedAt?: string;
}

type SortField = "name" | "status" | "startedAt" | "completedAt";
type SortOrder = "asc" | "desc";

export const WorkflowOverview: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(["workflows", "projects", "common"]);
  const {
    getWorkflowLabel,
    getWorkflowsLabel,
    canManageWorkflows,
    isAdmin,
    isManager,
  } = useAuth();

  // Company-specific terminology
  const workflowText = getWorkflowLabel();
  const workflowTextLower = workflowText.toLocaleLowerCase("tr-TR");
  const workflowsText = getWorkflowsLabel();

  const [workflow, setWorkflow] = useState<Workflow | null>(null);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState<WorkflowInstanceProject[]>([]);
  const [projectFormElements, setProjectFormElements] = useState<
    ProjectFormElement[]
  >([]);
  const [projectManagerIds, setProjectManagerIds] = useState<number[]>([]);
  const [showEditDetailsModal, setShowEditDetailsModal] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [showStartProjectModal, setShowStartProjectModal] = useState(false);

  const canManageWorkflow = canManageWorkflows();
  const canAssignProjectManagers = isAdmin() || isManager();
  const canDeleteWorkflow = isAdmin() || isManager();

  // Table state
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [sortField, setSortField] = useState<SortField>("startedAt");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  const loadWorkflowProjects = useCallback(async (workflowId: number) => {
    const allInstances = await apiService.getWorkflowInstances();
    const workflowProjects = allInstances
      .filter((instance: any) => instance.templateWorkflowId === workflowId)
      .map((instance: any) => {
        // Get project name from workflowContainerFormData.name first
        let projectName = "Unnamed Project";
        if (instance.workflowContainerFormData?.name) {
          projectName = instance.workflowContainerFormData.name;
        } else if (instance.workflowContainerName) {
          projectName = instance.workflowContainerName;
        } else if (instance.name) {
          projectName = instance.name;
        }

        return {
          id: instance.id,
          name: projectName,
          description: instance.description,
          status: instance.status,
          workflowContainerId: instance.workflowContainerId,
          templateWorkflowId: instance.templateWorkflowId,
          startedAt: instance.startedAt,
          completedAt: instance.completedAt,
        };
      });
    setProjects(workflowProjects);
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const workflowId = parseInt(id, 10);
        const data = await apiService.getWorkflowById(workflowId);
        setWorkflow(data);

        if (canAssignProjectManagers) {
          try {
            const ids = await apiService.getWorkflowProjectManagers(workflowId);
            setProjectManagerIds(ids);
          } catch (error) {
          }
        }

        // Load projects (WorkflowInstances) created from this workflow
        await loadWorkflowProjects(workflowId);

        // Load project form elements from workflow container
        try {
          let formElementsFound = false;

          // Strategy 1: Try to get from WorkflowContainer's formElements
          if (data.workflowContainerId) {
            try {
              const container = await apiService.getWorkflowContainerById(
                data.workflowContainerId
              );
              if (
                container?.formElements &&
                Array.isArray(container.formElements) &&
                container.formElements.length > 0
              ) {
                setProjectFormElements(container.formElements);
                formElementsFound = true;
              }
            } catch (e) {
            }
          }

          if (!formElementsFound) {
            // Strategy 2: Try to get from WorkflowTemplate by ID
            try {
              const templateById = await apiService.getWorkflowTemplateById(
                workflowId
              );
              if (templateById?.workflowContainerFormElements) {
                try {
                  const parsed = JSON.parse(
                    templateById.workflowContainerFormElements
                  );
                  if (Array.isArray(parsed) && parsed.length > 0) {
                    setProjectFormElements(parsed);
                    formElementsFound = true;
                  }
                } catch (e) {
                }
              }
            } catch (templateError) {
            }
          }

          if (!formElementsFound && data.workflowContainerId) {
            // Strategy 3: Try to get from WorkflowContainer using getLastWorkflowFormData
            try {
              const formDataResponse = await apiService.getLastWorkflowFormData(
                data.workflowContainerId
              );
              if (
                formDataResponse.formElements &&
                Array.isArray(formDataResponse.formElements) &&
                formDataResponse.formElements.length > 0
              ) {
                // Convert container form elements to ProjectFormElement format
                const convertedElements = formDataResponse.formElements.map(
                  (el: any) => ({
                    id: el.id || `element_${Date.now()}_${Math.random()}`,
                    type: el.type,
                    label: el.label,
                    required: el.required,
                    placeholder: el.placeholder,
                    options: el.options,
                    validation: el.validation,
                  })
                );
                setProjectFormElements(convertedElements);
                formElementsFound = true;
              }
            } catch (containerError) {
            }
          }

          if (!formElementsFound) {
            // Strategy 4: Search all templates by name
            try {
              const templates = await apiService.getAllWorkflowTemplates();
              const matchingTemplate = templates.find(
                (template: any) =>
                  template.title === data.name || template.name === data.name
              );

              if (matchingTemplate?.workflowContainerFormElements) {
                try {
                  const parsed = JSON.parse(
                    matchingTemplate.workflowContainerFormElements
                  );
                  if (Array.isArray(parsed) && parsed.length > 0) {
                    setProjectFormElements(parsed);
                  }
                } catch (e) {
                }
              }
            } catch (searchError) {
            }
          }
        } catch (error) {
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id, loadWorkflowProjects, canAssignProjectManagers]);

  // Filter and sort logic
  const filteredAndSortedProjects = useMemo(() => {
    let filtered = projects;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (project) =>
          project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          project.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          project.status.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (statusFilter !== "ALL") {
      filtered = filtered.filter((project) => project.status === statusFilter);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];

      if (sortField === "startedAt" || sortField === "completedAt") {
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
      } else if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue?.toLowerCase() || "";
      }

      if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
      if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [projects, searchTerm, statusFilter, sortField, sortOrder]);

  // Pagination logic
  const totalPages = Math.ceil(filteredAndSortedProjects.length / itemsPerPage);
  const paginatedProjects = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedProjects.slice(
      startIndex,
      startIndex + itemsPerPage
    );
  }, [filteredAndSortedProjects, currentPage, itemsPerPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, itemsPerPage]);

  // Early returns AFTER all hooks
  if (loading) {
    return (
      <div className="app-page flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!workflow) {
    return (
      <div className="app-page flex items-center justify-center">
        <div className="text-center space-y-4">
          <h2 className="text-xl font-semibold text-slate-800">
            {t("workflows:notFound", "Workflow bulunamadı")}
          </h2>
          <button
            onClick={() => navigate("/workflows")}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t("workflows:backToList", "Workflow listesine dön")}
          </button>
        </div>
      </div>
    );
  }

  const handleEditDetails = () => {
    setShowEditDetailsModal(true);
  };

  const handleDelete = () => {
    setIsDeleteModalOpen(true);
  };

  const handleAddMembers = () => {
    if (!workflow?.workflowContainerId) {
      toast.error(t("errors:loadFailed"));
      return;
    }
    navigate(`/project-workflows/${workflow.workflowContainerId}/add-member`);
  };

  const handleCreateProject = () => {
    if (!workflow?.workflowContainerId) {
      toast.error(t("errors:loadFailed"));
      return;
    }
    setShowStartProjectModal(true);
  };

  const confirmDelete = async () => {
    if (!workflow) return;

    try {
      await apiService.deleteWorkflow(workflow.id);
      toast.success(t("workflows:deleteSuccess", `${workflowText} başarıyla silindi`));
      navigate("/workflows");
    } catch (error) {
      toast.error(
        t("workflows:deleteError", `${workflowText} silinirken bir hata oluştu`)
      );
    } finally {
      setIsDeleteModalOpen(false);
    }
  };

  const handleSaveDetails = async (formData: any) => {
    try {
      if (!workflow) return;

      // 1. Update Workflow Name (in workflows table)
      if (formData.workflowName && formData.workflowName !== workflow.name) {
        await apiService.updateWorkflow(workflow.id, {
          name: formData.workflowName,
          workflowContainerId: workflow.workflowContainerId,
        });
        setWorkflow((prev) =>
          prev
            ? {
                ...prev,
                name: formData.workflowName,
              }
            : null
        );
      }

      // 2. Update Project Form Elements (in workflow_container table)
      if (workflow.workflowContainerId) {
        // Fetch current container
        const currentProject = await apiService.getWorkflowContainerById(
          workflow.workflowContainerId
        );

        // Update form elements
        const updatedProject = {
          ...currentProject,
          formElements: formData.elements,
        };

        await apiService.updateProject(
          workflow.workflowContainerId,
          updatedProject
        );
      }

      if (
        canAssignProjectManagers &&
        Array.isArray(formData.projectManagerIds) &&
        workflow.id
      ) {
        await apiService.setWorkflowProjectManagers(
          workflow.id,
          formData.projectManagerIds
        );
        setProjectManagerIds(formData.projectManagerIds);
      }

      // Update local state
      setProjectFormElements(formData.elements);
      setShowEditDetailsModal(false);
      toast.success(
        t("workflows:detailsUpdated", "Detaylar başarıyla güncellendi")
      );
    } catch (error) {
      toast.error(t("errors:updateFailed", "Güncelleme başarısız"));
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handleViewProjectDetail = (project: WorkflowInstanceProject) => {
    navigate(`/project-instances/${project.id}`);
  };

  return (
    <div className="app-page bg-gradient-to-br from-slate-50 via-blue-50/40 to-indigo-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-10">
        {/* Back */}
        <button
          onClick={() => navigate("/workflows")}
          className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900 mb-4"
        >
          <ArrowLeftIcon className="w-4 h-4 mr-1" />
          {t("workflows:backToList", "Workflow listesine dön")}
        </button>

        {/* Header Card */}
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl border border-white/60 p-5 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-5">
            <div className="flex items-start space-x-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white shadow-lg">
                <span className="text-lg font-bold">
                  {workflow.name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">
                    {workflow.name}
                  </h1>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-3">
              {canManageWorkflow && (
                <>
                  <button
                    onClick={handleAddMembers}
                    className="inline-flex items-center px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-sm font-medium shadow-sm transition-all"
                  >
                    <UserPlusIcon className="w-4 h-4 mr-2" />
                    {t("projects:addMember")}
                  </button>
                  <button
                    onClick={handleCreateProject}
                    className="inline-flex items-center px-4 py-2 rounded-lg bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white text-sm font-medium shadow-sm transition-all"
                  >
                    <RocketLaunchIcon className="w-4 h-4 mr-2" />
                    {t("projects:createProject")}
                  </button>
                </>
              )}
              <button
                onClick={handleEditDetails}
                className="inline-flex items-center px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium shadow-sm transition-colors"
              >
                <Cog6ToothIcon className="w-4 h-4 mr-2" />
                {t("workflows:editDetails", "Detayları Düzenle")}
              </button>
              {canDeleteWorkflow && (
                <button
                  onClick={handleDelete}
                  className="inline-flex items-center px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium shadow-sm transition-colors"
                >
                  <TrashIcon className="w-4 h-4 mr-2" />
                  {t("common:delete", "Sil")}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Content placeholder for future stats / nodes summary */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/60 p-5">
            <h2 className="text-sm font-semibold text-slate-700 mb-4">
              {t("workflows:workflowActivities", `${workflowText} Aktiviteleri`)}
            </h2>
            <div className="space-y-2">
              {(() => {
                const activityNodes =
                  workflow.nodes?.filter(
                    (node: any) =>
                      node.type === "ACTIVITY" || node.type === "activity"
                  ) || [];

                if (activityNodes.length === 0) {
                  return (
                    <p className="text-sm text-slate-500 text-center py-4">
                      {t(
                        "workflows:noActivitiesInWorkflow",
                        `Bu ${workflowTextLower}nda atanmış bir aktivite yok`
                      )}
                    </p>
                  );
                }

                return activityNodes.map((node: any, index: number) => (
                  <div
                    key={node.id || index}
                    className="p-3 bg-slate-50 rounded-lg border border-slate-200"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-sm font-medium text-slate-900">
                          {node.data?.label ||
                            node.label ||
                            `Aktivite ${index + 1}`}
                        </h3>
                        {(node.data?.description || node.description) && (
                          <p className="text-xs text-slate-600 mt-1">
                            {node.data?.description || node.description}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ));
              })()}
            </div>
          </div>

          <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/60 p-5">
            <h2 className="text-sm font-semibold text-slate-700 mb-4">
              {t("workflows:structureSummary", "Yapı Özeti")}
            </h2>
            <div className="grid grid-cols-2 gap-4 text-sm text-slate-700">
              <div className="bg-slate-50 rounded-xl p-4 flex flex-col">
                <span className="text-xs text-slate-500 mb-1">
                  {t("workflows:nodes", "Node sayısı")}
                </span>
                <span className="text-xl font-bold">
                  {workflow.nodes?.length || 0}
                </span>
              </div>
              <div className="bg-slate-50 rounded-xl p-4 flex flex-col">
                <span className="text-xs text-slate-500 mb-1">
                  {t("workflows:edges", "Bağlantı sayısı")}
                </span>
                <span className="text-xl font-bold">
                  {workflow.edges?.length || 0}
                </span>
              </div>
            </div>
            <p className="mt-4 text-xs text-slate-500">
              {t(
                "workflows:overviewHint",
                "Detaylı düzenleme için designer'a geçebilirsiniz."
              )}
            </p>
          </div>
        </div>

        {/* Projects created from this workflow */}
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/60 p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <FolderIcon className="w-5 h-5 text-blue-600" />
            <h2 className="text-sm font-semibold text-slate-700">
              {t(
                "workflows:createdProjects",
                `Bu ${workflowText}nden Oluşturulan Projeler`
              )}{" "}
              ({projects.length})
            </h2>
          </div>

          {projects.length === 0 ? (
            <p className="text-sm text-slate-500">
              {t(
                "workflows:noProjectsYet",
                `Henüz bu ${workflowTextLower}nden proje oluşturulmamış.`
              )}
            </p>
          ) : (
            <>
              {/* Search and Filter Bar */}
              <div className="mb-4 flex flex-col sm:flex-row gap-3">
                {/* Search */}
                <div className="flex-1 relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <input
                    type="text"
                    placeholder={t("projects:searchProjects", "Proje ara...")}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm("")}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>

                {/* Status Filter */}
                <div className="flex items-center gap-2">
                  <FunnelIcon className="h-5 w-5 text-slate-500" />
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  >
                    <option value="ALL">{t("common:allStatuses", "Tüm Durumlar")}</option>
                    <option value="STARTED">{t("common:status_STARTED", "Başladı")}</option>
                    <option value="IN_PROGRESS">{t("common:status_IN_PROGRESS", "Devam Ediyor")}</option>
                    <option value="COMPLETED">{t("common:status_COMPLETED", "Tamamlandı")}</option>
                    <option value="CANCELLED">{t("common:status_CANCELLED", "İptal Edildi")}</option>
                  </select>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th
                        onClick={() => handleSort("name")}
                        className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors"
                      >
                        <div className="flex items-center gap-1">
                          {t("projects:projectName", "Proje Adı")}
                          {sortField === "name" && (
                            sortOrder === "asc" ? (
                              <ChevronUpIcon className="h-4 w-4" />
                            ) : (
                              <ChevronDownIcon className="h-4 w-4" />
                            )
                          )}
                        </div>
                      </th>
                      <th
                        onClick={() => handleSort("status")}
                        className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors"
                      >
                        <div className="flex items-center gap-1">
                          {t("common:status", "Durum")}
                          {sortField === "status" && (
                            sortOrder === "asc" ? (
                              <ChevronUpIcon className="h-4 w-4" />
                            ) : (
                              <ChevronDownIcon className="h-4 w-4" />
                            )
                          )}
                        </div>
                      </th>
                      <th
                        onClick={() => handleSort("startedAt")}
                        className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors"
                      >
                        <div className="flex items-center gap-1">
                          {t("projects:startDate", "Başlangıç")}
                          {sortField === "startedAt" && (
                            sortOrder === "asc" ? (
                              <ChevronUpIcon className="h-4 w-4" />
                            ) : (
                              <ChevronDownIcon className="h-4 w-4" />
                            )
                          )}
                        </div>
                      </th>
                      <th
                        onClick={() => handleSort("completedAt")}
                        className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors"
                      >
                        <div className="flex items-center gap-1">
                          {t("projects:endDate", "Bitiş")}
                          {sortField === "completedAt" && (
                            sortOrder === "asc" ? (
                              <ChevronUpIcon className="h-4 w-4" />
                            ) : (
                              <ChevronDownIcon className="h-4 w-4" />
                            )
                          )}
                        </div>
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-slate-700 uppercase tracking-wider">
                        {t("common:actions", "İşlemler")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-200">
                    {paginatedProjects.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center text-sm text-slate-500">
                          {t("projects:noProjectsFound", "Proje bulunamadı")}
                        </td>
                      </tr>
                    ) : (
                      paginatedProjects.map((project) => (
                        <tr
                          key={project.id}
                          className="hover:bg-slate-50 transition-colors"
                        >
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-slate-900">
                              {project.name}
                            </div>
                            {project.description && (
                              <div className="text-xs text-slate-500 max-w-xs truncate">
                                {project.description}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                              {t(`common:status_${project.status}`, project.status)}
                            </span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-500">
                            {project.startedAt
                              ? new Date(project.startedAt).toLocaleDateString("tr-TR")
                              : "-"}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-500">
                            {project.completedAt
                              ? new Date(project.completedAt).toLocaleDateString("tr-TR")
                              : "-"}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              onClick={() => handleViewProjectDetail(project)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                            >
                              <EyeIcon className="h-4 w-4" />
                              {t("common:view", "Görüntüle")}
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {filteredAndSortedProjects.length > 0 && (
                <div className="mt-4 flex items-center justify-between">
                  <div className="text-sm text-slate-700">
                    {t("common:showingResults", {
                      start: (currentPage - 1) * itemsPerPage + 1,
                      end: Math.min(
                        currentPage * itemsPerPage,
                        filteredAndSortedProjects.length
                      ),
                      total: filteredAndSortedProjects.length,
                      defaultValue: `${(currentPage - 1) * itemsPerPage + 1}-${Math.min(
                        currentPage * itemsPerPage,
                        filteredAndSortedProjects.length
                      )} / ${filteredAndSortedProjects.length}`,
                    })}
                  </div>

                  <div className="flex items-center gap-2">
                    {/* Items per page */}
                    <select
                      value={itemsPerPage}
                      onChange={(e) => setItemsPerPage(Number(e.target.value))}
                      className="px-2 py-1 border border-slate-300 rounded text-sm"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>

                    {/* Page navigation */}
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronLeftIcon className="h-5 w-5" />
                      </button>

                      <span className="px-3 py-1 text-sm text-slate-700">
                        {currentPage} / {totalPages}
                      </span>

                      <button
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronRightIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Project form elements */}
        {projectFormElements.length > 0 && (
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/60 p-5">
            <div className="flex items-center gap-3 mb-4">
              <DocumentTextIcon className="w-5 h-5 text-indigo-600" />
              <h2 className="text-sm font-semibold text-slate-700">
                {t(
                  "workflows:projectFormElements",
                  "Proje Üretilirken Kullanılacak Bilgiler"
                )}
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {projectFormElements.map((element, index) => (
                <div
                  key={element.id || index}
                  className="p-3 bg-slate-50 rounded-lg"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-1.5 min-w-0 flex-1">
                      <span className="text-sm font-medium text-slate-900 truncate">
                        {element.label}
                      </span>
                      {element.required && (
                        <span className="flex-shrink-0 text-xs px-1.5 py-0.5 bg-red-100 text-red-700 rounded">
                          {t("common:required", "Zorunlu")}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-xs text-slate-600 mt-1.5">
                    {t(`forms:fieldTypes.${element.type}`, element.type)}
                  </div>
                  {element.placeholder && (
                    <div className="text-xs text-slate-500 mt-1">
                      {element.placeholder}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Edit Details Modal */}
      {showEditDetailsModal && workflow && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl h-[95vh] flex flex-col overflow-hidden">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-amber-50 to-orange-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <Cog6ToothIcon className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      {t("workflows:editDetails", "Detayları Düzenle")}
                    </h2>
                    <p className="text-sm text-gray-600 mt-1">
                      {t(
                        "workflows:editDetailsDescription",
                        "Proje form alanlarını düzenleyin"
                      )}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowEditDetailsModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white/50 rounded-lg transition-colors"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Form Builder Content */}
            <div className="flex-1 overflow-hidden">
              <ProjectFormBuilder
                key={`edit-details-${workflow.id}-${projectFormElements.length}`}
                initialData={{
                  workflowName: workflow.name,
                  formName: workflow.name,
                  elements: projectFormElements,
                  elementOrder: JSON.stringify(
                    projectFormElements.map((el) => el.id)
                  ),
                  projectManagerIds,
                }}
                isEditMode={true}
                onSave={handleSaveDetails}
                onCancel={() => setShowEditDetailsModal(false)}
              />
            </div>
          </div>
        </div>
      )}

      {showStartProjectModal && workflow?.workflowContainerId && (
        <WorkflowStartModal
          isOpen={showStartProjectModal}
          onClose={() => setShowStartProjectModal(false)}
          projectId={workflow.workflowContainerId}
          workflowId={workflow.id}
          onSuccess={async () => {
            await loadWorkflowProjects(workflow.id);
          }}
        />
      )}

      <ConfirmDialog
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title={t("workflows:deleteTitle", `${workflowText}nü Sil`)}
        message={t(
          "workflows:deleteConfirm",
          `Bu ${workflowTextLower}nü silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`
        )}
        confirmText={t("common:delete", "Sil")}
        cancelText={t("common:cancel", "İptal")}
        type="danger"
      />
    </div>
  );
};
